/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react'
import React, { useMemo, useRef } from 'react'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import {
    LayerGroup,
    LayersControl,
    MapContainer,
    Marker,
    CircleMarker,
    Polygon,
    Popup,
    TileLayer,
    useMap,
} from 'react-leaflet'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { getCatchmentArea, getPointOfSale } from '../../services/PointOfSale/PointOfSale.service'
import { useSearchParams } from 'react-router-dom-v5-compat'
import { Filters } from './Filters'

import usePlatform from '../Shared/PlatformProvider/usePlatforms'
import { HeatmapLayer } from './HeatmapLayer'
import { Summary } from './Summary'


const pointOfSaleIcon = new L.Icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
})

const canvasRenderer = L.canvas({padding: 0.5})

const progressSlide = keyframes`
    0% {
        left: -40%;
        width: 40%;
    }
    60% {
        left: 60%;
        width: 40%;
    }
    100% {
        left: 100%;
        width: 40%;
    }
`

const FitBounds = ({bounds, padding = [50, 50]}) => {
    const map = useMap()
    const prevBoundsRef = useRef(null)
    if (bounds && bounds !== prevBoundsRef.current) {
        prevBoundsRef.current = bounds
        map.fitBounds(bounds, {padding})
    }
    return null
}

function getPlatformColor(platformId) {
    switch (platformId) {
        case '2e9a26e0-48fc-48a5-b59c-8559130f44e6':
            return '#00ccbc'
        case '3dd792fe-8267-424b-afd0-3d4212b1bd45':
            return '#38b05c'
        default:
            return 'blue'
    }
}

// TODO: return platform module from backend
const PLATFORM_ID_MODULE_MAP = {
    '2e9a26e0-48fc-48a5-b59c-8559130f44e6': 'deliveroo',
    '3dd792fe-8267-424b-afd0-3d4212b1bd45': 'ubereats',
}

function getPlatformIcon(platformId) {
    const platformModule = PLATFORM_ID_MODULE_MAP[platformId] ?? 'unknown'
    return L.divIcon({
        html: `<img src="/img/platforms/${platformModule}.jpg" alt="${platformModule}" style="width:24px;height:24px;border-radius:50%;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4);object-fit:cover;" />`,
        className: '',
        iconSize: [24, 24],
        iconAnchor: [12, 12],
    })
}

const CatchmentArea = () => {
    const [searchParams] = useSearchParams()
    const posId = searchParams.get('merchant')
    const from = searchParams.get('from')
    const to = searchParams.get('to')
    const platformIds = searchParams.getAll('platform')
    const {get: getPlatform} = usePlatform()

    const {data: pointOfSale} = useQuery({
        queryKey: ['pointofsales', posId],
        queryFn: () => getPointOfSale(posId),
        enabled: !!posId,
    })

    const {data, isFetching} = useQuery({
        queryKey: ['pointofsales', posId, 'catchment-area', from, to, platformIds],
        queryFn: async ({ signal }) => getCatchmentArea(posId, {from, to, platformIds}, signal),
        enabled: !!posId,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
    })

    const heatmapPoints = useMemo(
      () => data?.heatmap?.map(({latitude, longitude, intensity}) => [latitude, longitude, intensity]) ?? [],
      [data?.heatmap],
    )

    const bounds = useMemo(() => {
        const latLngs = []
        if (pointOfSale?.location) {
            latLngs.push([pointOfSale.location.latitude, pointOfSale.location.longitude])
        }
        data?.points?.forEach(p => latLngs.push([p.location.latitude, p.location.longitude]))
        if (latLngs.length === 0) return null
        return L.latLngBounds(latLngs).pad(0.1)
    }, [pointOfSale, data])

    const parisLocation = [48.8566, 2.3522]
    const centerLocation = pointOfSale?.location ?
      [pointOfSale.location.latitude, pointOfSale.location.longitude] :
      parisLocation

    return (
      <>
          <Filters/>

          <Summary/>

          <div css={css`
              position: relative;
              isolation: isolate;
          `}
          >
              {isFetching && (
                <div css={css`
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 2px;
                    overflow: hidden;
                    background: transparent;
                    z-index: 1000;

                    &::after {
                        content: '';
                        position: absolute;
                        top: 0;
                        height: 100%;
                        background: grey;
                        animation: ${progressSlide} 1.4s ease-in-out infinite;
                    }
                `}/>
              )}
              <MapContainer
                center={centerLocation}
                zoom={13}
                scrollWheelZoom
                css={css`
                    width: 100%;
                    height: 70vh;
                `}
              >
                  <LayersControl position="bottomright">
                      <LayersControl.BaseLayer name="OpenStreetMap">
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                      </LayersControl.BaseLayer>
                      <LayersControl.BaseLayer checked name="Esri World Street Map">
                          <TileLayer
                            attribution="Tiles &copy; Esri &mdash; Source: Esri, DeLorme, NAVTEQ, USGS, Intermap, iPC, NRCAN, Esri Japan, METI, Esri China (Hong Kong), Esri (Thailand), TomTom, 2012"
                            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
                          />
                      </LayersControl.BaseLayer>
                      <LayersControl.BaseLayer name="Stadia Outdoors">
                          <TileLayer
                            attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}{r}.png"
                          />
                      </LayersControl.BaseLayer>
                  </LayersControl>

                  {pointOfSale && pointOfSale.location && (
                    <Marker position={[pointOfSale.location.latitude, pointOfSale.location.longitude]}
                            icon={pointOfSaleIcon}>
                        <Popup>
                            {pointOfSale.name}
                        </Popup>
                    </Marker>
                  )}

                  <LayersControl position="topright">
                      {data?.points && data.points.length > 0 && (
                        <LayersControl.Overlay checked name="Points">
                            <LayerGroup>
                                {data?.points?.map(point => (
                                  <CircleMarker
                                    key={`${point.date}-${point.location.longitude}-${point.location.latitude}`}
                                    center={[point.location.latitude, point.location.longitude]}
                                    radius={5}
                                    renderer={canvasRenderer}
                                    pathOptions={{
                                        color: 'white',
                                        fillColor: getPlatformColor(point.platformId),
                                        fillOpacity: 1,
                                        weight: 1,
                                    }}
                                  />
                                ))}
                            </LayerGroup>
                        </LayersControl.Overlay>
                      )}

                      {data?.points && data.points.length > 0 && (
                        <LayersControl.Overlay name="Logos">
                            <LayerGroup>
                                {data?.points?.map(point => (
                                  <Marker
                                    key={`logo-${point.date}-${point.location.longitude}-${point.location.latitude}`}
                                    position={[point.location.latitude, point.location.longitude]}
                                    icon={getPlatformIcon(point.platformId)}
                                  />
                                ))}
                            </LayerGroup>
                        </LayersControl.Overlay>
                      )}

                      {data?.polygons?.map(polygon => {
                          const label = polygon.platformId
                            ? (getPlatform(polygon.platformId)?.name ?? polygon.platformId)
                            : 'Toutes plateformes'
                          const positions = polygon.hull.coordinates[0].map(([lng, lat]) => [lat, lng])
                          return (
                            <LayersControl.Overlay checked key={polygon.platformId ?? 'all'} name={label}>
                                <Polygon
                                  positions={positions}
                                  pathOptions={{
                                      color: getPlatformColor(polygon.platformId),
                                      fillColor: getPlatformColor(polygon.platformId),
                                      fillOpacity: 0.2,
                                      weight: 2,
                                  }}
                                />
                            </LayersControl.Overlay>
                          )
                      })}

                      {data?.heatmap && data.heatmap.length > 0 && (
                        <LayersControl.Overlay checked name="Heatmap">
                            <HeatmapLayer
                              points={heatmapPoints}
                              radius={25}
                              blur={15}
                              minOpacity={0.4}
                              gradient={{
                                  0.4: 'blue',
                                  0.6: 'lime',
                                  0.7: 'yellow',
                                  0.8: 'orange',
                                  1.0: 'red',
                              }}
                            />
                        </LayersControl.Overlay>
                      )}
                  </LayersControl>

                  <FitBounds bounds={bounds}/>
              </MapContainer>
          </div>
      </>
    )
}

export { CatchmentArea }
